/**
 * useParticipantsWsStore — reactive participant-per-hearing state populated
 * by /ws/officer participant.* events.
 *
 * The participants card in HearingRoomView + HearingDetailView reads from
 * this store to reflect real-time call state (dialing, ringing, joined,
 * muted, held, disconnected) without polling.
 *
 * Same non-authoritative pattern as wsHearings — REST hearings/participants
 * endpoints are the source of truth. This store overlays real-time state
 * changes on top.
 */
import { defineStore } from 'pinia'
import { ref } from 'vue'

/** Call state values that arrive over WS. */
export type ParticipantCallState =
  | 'idle'
  | 'dialing'
  | 'ringing'
  | 'answered'
  | 'joined'
  | 'left'
  | 'disconnected'
  | 'no_answer'
  | 'busy'
  | 'failed'

export interface ParticipantWsPatch {
  participant_id: string
  hearing_id: string
  call_state?: ParticipantCallState
  is_muted?: boolean
  is_on_hold?: boolean
  is_talking?: boolean
  call_id?: string | null
  last_event_at?: string
  /** Optional identity fields that may arrive on `participant.added` / `.updated`. */
  name?: string | null
  role?: string | null
  phone?: string | null
  [key: string]: any
}

export const useParticipantsWsStore = defineStore('wsParticipants', () => {
  /** `byHearingId[hearingId][participantId] = ParticipantWsPatch` */
  const byHearingId = ref<Record<string, Record<string, ParticipantWsPatch>>>({})

  function _ensureHearing(hearingId: string) {
    if (!byHearingId.value[hearingId]) {
      byHearingId.value = { ...byHearingId.value, [hearingId]: {} }
    }
  }

  function applyPatch(patch: ParticipantWsPatch) {
    if (!patch.hearing_id || !patch.participant_id) return
    _ensureHearing(patch.hearing_id)
    const existing = byHearingId.value[patch.hearing_id][patch.participant_id] || {
      participant_id: patch.participant_id,
      hearing_id: patch.hearing_id,
    }
    byHearingId.value = {
      ...byHearingId.value,
      [patch.hearing_id]: {
        ...byHearingId.value[patch.hearing_id],
        [patch.participant_id]: { ...existing, ...patch },
      },
    }
  }

  function remove(hearingId: string, participantId: string) {
    if (!byHearingId.value[hearingId]) return
    const next = { ...byHearingId.value[hearingId] }
    delete next[participantId]
    byHearingId.value = { ...byHearingId.value, [hearingId]: next }
  }

  function clearHearing(hearingId: string) {
    const next = { ...byHearingId.value }
    delete next[hearingId]
    byHearingId.value = next
  }

  function reset() {
    byHearingId.value = {}
  }

  /** Read helper for views — returns the array of patches for a hearing, or []. */
  function participantsFor(hearingId: string): ParticipantWsPatch[] {
    return Object.values(byHearingId.value[hearingId] || {})
  }

  return {
    byHearingId,
    applyPatch,
    remove,
    clearHearing,
    reset,
    participantsFor,
  }
})
